<script setup lang="ts">
import type { EsProductFragment, StoreStockStatusFragment } from '#graphql-operations'

const dialogName = 'store-picker-dialog-product-stock'

const props = withDefaults(defineProps<{
  product: EsProductFragment
  storeStock: StoreStockStatusFragment
  showAvailability?: boolean
}>(), {
  showAvailability: true,
})
const { allocatedStore } = useStorelocator()

const inStockCities = computed(() => {
  if (!props.storeStock) return []
  return props.storeStock.stores
    ?.filter((store: any) => store.status === 'in-stock')
    ?.map((store: any) => store.location?.name) || []
})

const onlyInCity = computed(() => inStockCities.value?.[0])

const hasAllocatedStoreStock = computed<boolean | null>(() => {
  if (!allocatedStore.value) {
    return null
  }
  return props.storeStock.stores?.find((item: any) => item.code === allocatedStore.value?.code)?.status === 'in-stock'
})

const qtyStoresWithStock = computed<number>(() => {
  return props.storeStock?.stores?.filter(item => item?.status === 'in-stock')?.length || 0
})

const storePickerDialog = useUiDialog(dialogName)

const hasDeliveryTime = computed(() => {
  return props.product.stockBackorder && (props.product.deliveryTime ?? 0) > 0
})

const isAccessory = computed(() => props.product.groupCode === 'Accessoires')

const weeks = computed(() => Math.floor(props.product.deliveryTime! / 5))
</script>

<template>
  <slot :only-in-city="onlyInCity">
    <div v-if="showAvailability">
      <StorePickerDialog
        :name="dialogName"
        :store-stock="storeStock"
        :delivery-time="hasDeliveryTime ? $t('Expected delivery time from: {weeks} weeks', { weeks }) as string : undefined"
      >
        <template #default>
          <p class="stores-pickup__description">
            Bekijk hieronder welke winkels dit product op voorraad hebben<span v-if="!isAccessory"> in hun magazijn</span>. Bij deze winkels is het product te bestellen voor afhalen.
          </p>
          <div
            v-if="!product.saleType"
            class="stores-pickup__visual"
          >
            <img
              :src="product.image"
              alt="Product image"
              class="stores-pickup__visual-image"
            >
            <p class="stores-pickup__visual-text">
              {{ $t('Want to see and try out this product up close? You can do so in all our stores.') }}
            </p>
          </div>
        </template>
        <template #toggle>
          <span v-if="isAccessory">Alleen winkels met voorraad</span>
          <span v-else>Alleen winkels met magazijnvoorraad</span>
        </template>
      </StorePickerDialog>
      <ul
        v-if="storeStock?.status === 'all-backorder'"
        class="availability"
      >
        <li class="availability__stores availability__stores--all-backorder">
          {{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}
        </li>
      </ul>
      <ul
        v-else
        class="availability"
      >
        <template v-if="product.saleType !== 'OUTLET'">
          <li
            v-if="product.isPickupOnly"
            class="availability__stores availability__stores--all-backorder"
          >
            <b>{{ $t('Delivery not available') }}</b>
          </li>
          <li
            v-else-if="qtyStoresWithStock"
            class="availability__online availability__online--true"
          >
            <b>{{ $t('In stock online') }}</b>&nbsp;
            <span>{{ $t('Quick delivery') }}</span>
          </li>
          <li
            v-else-if="storeStock?.deliveryTime"
            class="availability__online availability__online--false"
          >
            <b>{{ $t('Out of stock online') }}</b>&nbsp;
            <span>{{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}</span>
          </li>
        </template>
        <li
          v-if="product.saleType === 'OUTLET' && onlyInCity"
          class="availability__stores availability__stores--true"
        >
          <b>{{ $t('Reserve and pickup only at') }}</b> {{ onlyInCity }}
        </li>
        <li
          v-else-if="product.saleType === 'SALE' && inStockCities.length === 1"
          class="availability__stores availability__stores--true"
        >
          <b>{{ $t('Order and pickup only at') }}</b> {{ onlyInCity }}
        </li>
        <li
          v-else-if="hasAllocatedStoreStock === null"
          class="availability__stores"
        >
          <b>{{ $t('Order and pickup') }}</b>&nbsp;
          <button
            type="button"
            @click="storePickerDialog.open()"
          >
            <span v-if="!qtyStoresWithStock && product.stockBackorder">{{ $t('Met levertijd bij alle winkels') }}</span>
            <span v-else>{{ $t('View store stock') }}</span>
          </button>
        </li>
        <li
          v-else-if="hasAllocatedStoreStock"
          class="availability__stores availability__stores--true"
        >
          <b>{{ $t('Order and pickup') }}</b>
          {{ $t('In stock at') }} {{ allocatedStore?.name }}&nbsp;
          <button
            type="button"
            @click="storePickerDialog.open()"
          >
            {{ $t('Select another store') }}
          </button>
        </li>
        <li
          v-else
          class="availability__stores availability__stores--false"
        >
          <span>
            <b>{{ $t('Order and pickup') }}</b>&nbsp;
            <template v-if="!qtyStoresWithStock && product.stockBackorder">Met levertijd bij</template>
            <template v-else>{{ $t('all-out-of-stock') }} bij</template>
            {{ allocatedStore?.name }}&nbsp;
          </span>
          <button
            type="button"
            @click="storePickerDialog.open()"
          >
            {{ $t('Select another store') }}
          </button>
        </li>
      </ul>
    </div>
  </slot>
</template>
